import path from 'path'

import _ from 'lodash'
import uuid from 'uuid'
import axios from 'axios'
import bluebird from 'bluebird'
import { gql } from '@apollo/client'
import { message } from 'antd'

import isCADFile from '~/lib/isCADFile'
import logger from '~/lib/logger'
import { encodeForHeader } from '~/lib/encodeForHeader'
import { isBlockedFileName } from '~/lib/fileExtensions'
import {
    UPLOAD_BLOCKED_ATTACHMENT_ERROR,
    UPLOAD_BLOCKED_SOME_ATTACHMENTS_ERROR,
} from '~/lib/flashMsgs'
import { AttachmentCreateType } from '~/__generated__/globalTypes'
import { getenv } from '~/settings'

export const FICTIV_SUPPORT_LINK = `${
    getenv('REGIONAL_CONFIGS')?.urls?.what_file_formats_does_fictiv_support
}`

export const FICTIV_TERMS_SERVICES = `${
    getenv('REGIONAL_CONFIGS')?.urls?.terms
}`
export const EAR_SUPPORT = `https://www.bis.doc.gov/index.php/regulations/export-administration-regulations-ear`
export const ITAR_SUPPORT = `https://www.pmddtc.state.gov/ddtc_public?id=ddtc_kb_article_page&sys_id= 24d528fddbfc930044f9ff621f961987`

const createQuoteMutation = gql`
    mutation createQuoteDropzone(
        $name: String
        $description: String
        $userId: Int
        $quoteType: QuoteType
        $teamId: Int
        $processId: String
    ) {
        createQuote(
            input: { name: $name, description: $description }
            userId: $userId
            type: $quoteType
            teamId: $teamId
            processId: $processId
        ) {
            id
            name
        }
    }
`

const createQuoteModelRevisionMutation = gql`
    mutation createQmrDropzone(
        $uploadMetadata: QuoteModelRevisionUploadInput!
        $quoteId: UUID!
        $quoteModelId: UUID
        $setAsSelectedForQuoteModel: Boolean!
        $userId: Int
    ) {
        createQuoteModelRevision(
            uploadMetadata: $uploadMetadata
            quoteId: $quoteId
            quoteModelId: $quoteModelId
            setAsSelectedForQuoteModel: $setAsSelectedForQuoteModel
            userId: $userId
        ) {
            id
            uploadUrl
            partRevision {
                id
                partId
                thumbnailUri
            }
        }
    }
`

const createQuoteAttachmentRevisionMutation = gql`
    mutation createQuoteAttachmentRevisionDropzone(
        $uploadMetadata: AttachmentInput!
        $quoteId: UUID!
        $quoteAttachmentId: UUID
        $attachmentType: String
        $productAttachmentId: String
    ) {
        createQuoteAttachmentRevision(
            uploadMetadata: $uploadMetadata
            quoteId: $quoteId
            quoteAttachmentId: $quoteAttachmentId
            attachmentType: $attachmentType
            productAttachmentId: $productAttachmentId
        ) {
            id
            attachment {
                ... on Attachment {
                    id
                    fileName
                    uploadUrl
                }
            }
            asset {
                id
                fileName
                uploadUrl
            }
        }
    }
`

export const finalizeUploadAndProcessPart = gql`
    mutation dropZoneFinalizeUploadAndProcessPart($partId: Int!) {
        finalizeUploadAndProcessPart(partId: $partId) {
            partId
            status
            reason
        }
    }
`

const markQuoteAttachmentRevisionUploadedMutation = gql`
    mutation markQuoteAttachmentRevisionUploadedDropzone($id: UUID!) {
        markQuoteAttachmentRevisionUploaded(id: $id) {
            id
            attachment {
                ... on Attachment {
                    id
                    fileName
                    downloadUrl
                }
            }
        }
    }
`

export const itarAcceptedDateTimeQuery = gql`
    query itarAcceptedDatetime($userId: Int!) {
        user(id: $userId) {
            id
            itarAcceptedDatetime
            email
        }
    }
`

export const updateItarAcceptedDatetimeMutation = gql`
    mutation updateItarAcceptedDatetime($userId: Int!) {
        user(id: $userId) {
            updateItarAcceptedDatetime {
                id
                itarAcceptedDatetime
            }
        }
    }
`

export function createQuote(userId, quoteType, teamId, processId) {
    return async (dispatch, getState, { client }) => {
        try {
            const { data } = await client.mutate({
                mutation: createQuoteMutation,
                variables: { userId, quoteType, teamId, processId },
            })

            return data.createQuote.id
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: userId
                        ? 'AdminUserQuotesPage::Failed to create quote'
                        : 'Failed to create quote',
                    userId,
                },
            })

            message.error('Failed to create quote')
            throw err
        }
    }
}

export function hydrateMetadata(file, units) {
    return Object.assign(file, {
        referenceId: uuid.v4(),
        fileHandle: file,
        contentType: file.type || 'application/octet-stream',
        units,
    })
}

export async function createQuoteModelRevision(
    { file, units, quoteId, userId },
    client,
    refetchQueries,
) {
    try {
        const result = await client.mutate({
            mutation: createQuoteModelRevisionMutation,
            variables: {
                uploadMetadata: {
                    referenceId: file.referenceId,
                    contentType: file.contentType,
                    fileName: file.name,
                    fileSize: file.size,
                    units,
                },
                quoteId,
                quoteModelId: null,
                setAsSelectedForQuoteModel: true,
                userId,
            },
            refetchQueries,
        })

        //return metadata to be used in uploadStagedPartFiles
        return {
            referenceId: file.referenceId,
            quoteId,
            quoteModelRevisionId: 'data.createQuoteModelRevision.id',
            fileName: file.fileName,
            fileSize: file.fileHandle.size,
            postUrl: _.get(result, 'data.createQuoteModelRevision.uploadUrl'),
            partId: _.get(
                result,
                'data.createQuoteModelRevision.partRevision.partId',
            ),
        }
    } catch (err) {
        logger.exception(err, {
            extra: {
                info: 'Failed to create quoteModelRevision during upload',
                quoteId,
                fileName: file.name,
                fileSize: file.size,
            },
        })

        throw err
    }
}

export function markQuoteAttachmentRevisionAsUploaded(
    { partUploadMetadata, quoteId },
    client,
    refetchQueries,
) {
    try {
        return client.mutate({
            mutation: markQuoteAttachmentRevisionUploadedMutation,
            variables: {
                id: partUploadMetadata.quoteAttachmentRevisionId,
            },
            refetchQueries,
        })
    } catch (err) {
        logger.exception(err, {
            extra: {
                info: 'Failed to mark quote attachment uploaded',
                quoteId,
                fileName: partUploadMetadata.fileName,
                quoteAttachmentRevisionId:
                    partUploadMetadata.quoteAttachmentRevisionId,
            },
        })

        throw err
    }
}

export async function createQuoteAttachmentRevision({ file, quoteId }, client) {
    try {
        const result = await client.mutate({
            mutation: createQuoteAttachmentRevisionMutation,
            variables: {
                uploadMetadata: {
                    mimeType: file.contentType,
                    extension: path.extname(file.name),
                    fileName: file.name,
                    type: AttachmentCreateType.user_uploaded,
                },
                quoteId,
                attachmentType: null,
                quoteAttachmentId: null,
                productAttachmentId: null,
            },
        })

        const quoteAttachmentRevisionId = _.get(
            result,
            'data.createQuoteAttachmentRevision.id',
        )
        const uploadUrl =
            _.get(
                result,
                'data.createQuoteAttachmentRevision.attachment.uploadUrl',
            ) ||
            _.get(result, 'data.createQuoteAttachmentRevision.asset.uploadUrl')

        return {
            quoteAttachmentRevisionId,
            contentType: file.type,
            fileHandle: file,
            fileName: file.name,
            uploadUrl,
        }
    } catch (err) {
        logger.exception(err, {
            extra: {
                info: 'Failed to create quoteAttachmentRevision during upload',
                quoteId,
                fileName: file.name,
            },
        })

        throw err
    }
}

export function uploadPartFile(partUploadMetadata, setProgress) {
    const { contentType, fileHandle, postUrl } = partUploadMetadata

    const onUploadProgress = progressEvent => {
        if (setProgress) {
            setProgress(progress => {
                const newProgress = { ...progress }
                let percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total,
                )

                newProgress[fileHandle.name].percentageComplete =
                    percentCompleted
                newProgress[fileHandle.name].totalBytes = progressEvent.total
                newProgress[fileHandle.name].numBytesDownloaded =
                    progressEvent.loaded

                return newProgress
            })
        }
    }

    return axios
        .put(postUrl, fileHandle, {
            withCredentials: false,
            headers: { 'Content-Type': contentType },
            onUploadProgress,
        })
        .catch(err => {
            logger.exception(err, {
                extra: {
                    info: 'Failed to upload part to s3',
                    fileHandle,
                    postUrl,
                },
            })

            throw err
        })
}

export function uploadAttachmentFile(uploadMetadata) {
    const { contentType, uploadUrl, fileHandle, fileName } = uploadMetadata

    return axios
        .put(uploadUrl, fileHandle, {
            withCredentials: false,
            headers: {
                'Content-Type': contentType,
                'Content-Disposition': `attachment; filename*=UTF-8''${encodeForHeader(
                    fileName,
                )}`,
            },
        })
        .catch(err => {
            logger.exception(err, {
                extra: {
                    info: 'Failed to upload attachment to s3',
                    fileName,
                    uploadUrl,
                },
            })

            throw err
        })
}

export async function managePartUpload(data, options, setProgress) {
    const { file, quoteId, units, userId } = data
    const { client, refetchQueries } = options

    const fullFile = hydrateMetadata(file, units)
    const partUploadMetadata = await createQuoteModelRevision(
        { file: fullFile, units, quoteId, userId },
        client,
        refetchQueries,
    )

    Object.assign(partUploadMetadata, {
        fileHandle: fullFile,
        contentType: fullFile.contentType,
    })

    await uploadPartFile(partUploadMetadata, setProgress)

    return client.mutate({
        mutation: finalizeUploadAndProcessPart,
        variables: {
            partId: partUploadMetadata.partId,
        },
    })
}

export async function manageAttachmentUpload(file, quoteId, options) {
    const { client, refetchQueries } = options

    const fullFile = hydrateMetadata(file)
    const partUploadMetadata = await createQuoteAttachmentRevision(
        { file: fullFile, quoteId },
        client,
    )

    Object.assign(partUploadMetadata, {
        fileHandle: fullFile,
        contentType: fullFile.contentType,
    })

    await uploadAttachmentFile(partUploadMetadata)
    await markQuoteAttachmentRevisionAsUploaded(
        { partUploadMetadata, quoteId },
        client,
        refetchQueries,
    )
}

export function uploadQuoteFiles(data, options, setProgress) {
    const { files, quoteId, units, userIdInParams } = data
    const blockedFiles = files.filter(file => isBlockedFileName(file.name))
    const userId = userIdInParams ? parseInt(userIdInParams) : null

    if (blockedFiles.length === files.length) {
        message.error(UPLOAD_BLOCKED_ATTACHMENT_ERROR)
    } else if (blockedFiles.length) {
        message.warning(UPLOAD_BLOCKED_SOME_ATTACHMENTS_ERROR)
    }
    const fileUploads = bluebird.map(
        files,
        async file => {
            if (isBlockedFileName(file.name)) {
                return true
            }
            try {
                return isCADFile(file)
                    ? await managePartUpload(
                          { file, quoteId, units, userId },
                          options,
                          setProgress,
                      )
                    : await manageAttachmentUpload(file, quoteId, options)
            } catch (err) {
                logger.exception(err, {
                    extra: {
                        isCADFile: isCADFile(file),
                        fileName: file.name,
                        fileSize: file.size,
                        quoteId,
                        units,
                    },
                })

                //Even though there was an error, let the promise continue on
                return true
            }
        },
        { concurrency: 10 },
    )

    return Promise.all(fileUploads).catch(err => {
        logger.exception(
            `Unhandled exception uploading ${files.length} files`,
            err,
            {
                extra: {
                    fileData: files.map(file => {
                        return {
                            fileName: file.name,
                            fileSize: file.size,
                        }
                    }),
                    quoteId,
                    units,
                },
            },
        )

        //Even though there was an error, let the promise continue on
        message.error(`There was an error uploading your parts`)
        return true
    })
}
